"use client";

import { motion } from "framer-motion";
import { Quote, Star } from "lucide-react";

import Section from "./ui/Section";

const testimonials = [
  {
    quote: "I liked the actual feel of cbt in this website",
    rating: 5,
    context: "Student exam feedback",
  },
  {
    quote:
      "The navigation through questions was extremely easy and clear..the test was really effective..it helped me improve my skills...would recommend it to my friends",
    rating: 5,
    context: "Student exam feedback",
  },
  {
    quote: "It was easy to use",
    rating: 4,
    context: "Student exam feedback",
  },
];

function Rating({ value }: { value: number }) {
  return (
    <div
      className="flex items-center gap-1"
      aria-label={`${value} out of 5 stars`}
    >
      {Array.from({ length: 5 }, (_, index) => (
        <Star
          key={index}
          className={`h-4 w-4 ${
            index < value ? "fill-brand-gold text-brand-gold" : "text-white/20"
          }`}
          aria-hidden="true"
        />
      ))}
      <span className="ml-2 text-xs font-semibold text-white/65">
        {value}/5
      </span>
    </div>
  );
}

export default function Testimonials() {
  return (
    <Section className="relative overflow-hidden bg-[#050B1A] py-20 lg:py-28">
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        aria-hidden="true"
      >
        <div className="absolute -left-40 top-[-12rem] h-[32rem] w-[32rem] rounded-full bg-brand/5 blur-[110px]" />
        <div className="absolute -right-40 bottom-[-14rem] h-[34rem] w-[34rem] rounded-full bg-brand-gold/10 blur-[120px]" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold/25 to-transparent" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mx-auto max-w-3xl text-center"
        >
          <span className="inline-flex rounded-full border border-brand-gold/25 bg-brand-gold/[0.08] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-brand-gold">
            Real student feedback
          </span>

          <h2 className="mt-7 text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl md:text-5xl">
            The exam experience,{" "}
            <span className="text-brand-gold">in students’ words.</span>
          </h2>

          <p className="mx-auto mt-6 max-w-2xl text-base leading-7 text-white/70 sm:text-lg sm:leading-8">
            Feedback from students who have used TCD for online examinations.
            We use their submitted comments rather than invented testimonials.
          </p>
        </motion.div>

        <div className="mt-12 grid gap-5 md:grid-cols-3 lg:mt-16 lg:gap-6">
          {testimonials.map((item, index) => (
            <motion.article
              key={item.quote}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{
                duration: 0.5,
                delay: index * 0.08,
                ease: "easeOut",
              }}
              whileHover={{ y: -4 }}
              className="group relative flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-white/10 bg-white/[0.045] p-6 shadow-[0_12px_40px_rgba(0,0,0,0.12)] transition-colors duration-300 hover:border-brand-gold/30 sm:p-7 lg:p-8"
            >
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold/50 to-transparent opacity-60 transition-opacity group-hover:opacity-100" />

              <div className="flex items-center justify-between gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-brand-gold/20 bg-brand-gold/10 text-brand-gold">
                  <Quote className="h-5 w-5" aria-hidden="true" />
                </div>
                <Rating value={item.rating} />
              </div>

              <blockquote className="mt-7 flex-1 text-base font-medium leading-8 text-white/90">
                “{item.quote}”
              </blockquote>

              <div className="mt-7 border-t border-white/10 pt-5">
                <p className="text-sm font-semibold text-white">
                  Anonymous student
                </p>
                <p className="mt-1 text-xs text-white/45">{item.context}</p>
              </div>
            </motion.article>
          ))}
        </div>

        <p className="mx-auto mt-6 max-w-3xl text-center text-xs leading-6 text-white/45">
          Comments are displayed anonymously.
        </p>
      </div>
    </Section>
  );
}
